import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as requestType from '../../../ressources/glossaires/glossaire_requetes';
import * as glossaire_type_de_graphe from '../../../ressources/glossaires/glossaire_type_de_graphe';
import * as actions from '../../../services/actions';
import DataGrid from '../../UI/DataGrid/DataGrid';
import Toolbar from '../../UI/Toolbar/Toolbar';
import S from './TableauGraphe.module.scss';

// Column definitions factory
const createColumn = (field, labelKey, width, formatter, flex) => ({
  field,
  headerName: checkMot(labelKey),
  width,
  sortable: true,
  align: 'center',
  headerAlign: 'center',
  ...(flex && { flex }),
  ...(formatter && { renderCell: (params) => formatter(params.value) }),
});

// Private/Public formatter
const formatPrivateStatus = (cell) => {
  switch (parseInt(cell)) {
    case 1:
      return checkMot('prive');
    case 0:
      return checkMot('public');
    default:
      return checkMot(cell);
  }
};

// Graph type formatter
const formatGraphType = (cell) => {
  const graphTypeKey = Object.keys(glossaire_type_de_graphe).find(
    (key) => glossaire_type_de_graphe[key] === parseInt(cell)
  );
  return checkMot(graphTypeKey);
};

// Column configuration
// [field, labelKey, width, formatter, flex]
const COLUMN_CONFIG = [
  ['graph_id', 'id', 100, null, null],
  ['project_id', 'projet', 120, null, null],
  ['graph_name', 'nom', 250, null, 1],
  ['private', 'propriete', 150, formatPrivateStatus, null],
  ['graph_type_id', 'type_de_graphe', 200, formatGraphType, 1],
];

const TableauGraphe = ({ creer, projetSelectione }) => {
  const dispatch = useDispatch();

  // Redux state
  const listeGraphes = useSelector((state) => state.graphe.graphs_list);
  const filtre_projects_list_id = useSelector(
    (state) => state.authentifie.filtre_projects_list_id
  );
  const project_id_url = useSelector(
    (state) => state.interfaceClient.project_id_url
  );
  const interfaceAdmin = useSelector(
    (state) => state.authentifie.interfaceAdmin
  );

  // Local state
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  // Memoized columns
  const columns = useMemo(() => {
    return COLUMN_CONFIG.map(([field, labelKey, width, formatter, flex]) => {
      const column = createColumn(field, labelKey, width, formatter, flex);

      // Hide ID and project columns if not admin
      if (!interfaceAdmin && (field === 'graph_id' || field === 'project_id')) {
        return { ...column, display: 'none' };
      }

      return column;
    });
  }, [interfaceAdmin]);

  // Effect for initial data fetch
  useEffect(() => {
    const projectIds =
      !isNaN(project_id_url) && project_id_url !== ''
        ? [parseInt(project_id_url)]
        : filtre_projects_list_id;

    if (projectIds.length > 0) {
      dispatch(actions.reqRecupererListeGraphes(projectIds));
    }
  }, [project_id_url, filtre_projects_list_id, dispatch]);

  // Handle row selection
  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      const selectedRow = listeGraphes?.find(
        (row) => newSelection[0] === row.graph_id
      );

      if (selectedRow) {
        projetSelectione(selectedRow);
      }
    },
    [listeGraphes, projetSelectione]
  );

  // Refresh handler
  const handleRefresh = useCallback(() => {
    const projectIds =
      !isNaN(project_id_url) && project_id_url !== ''
        ? [parseInt(project_id_url)]
        : filtre_projects_list_id;

    if (projectIds.length > 0) {
      dispatch(actions.reqRecupererListeGraphes(projectIds));
    }
  }, [dispatch, project_id_url, filtre_projects_list_id]);

  // Search handlers
  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  // Filtered rows with memoization
  const filteredRows = useMemo(() => {
    if (!listeGraphes) return [];

    return listeGraphes.filter((graph) => {
      // Filter by project
      const projectMatch = interfaceAdmin
        ? filtre_projects_list_id.includes(parseInt(graph.project_id))
        : parseInt(graph.project_id) === parseInt(project_id_url);

      if (!projectMatch) return false;
      if (!searchValue) return true;

      // Filter by search value
      const searchLower = searchValue.toLowerCase();
      return Object.values(graph).some((value) =>
        String(value).toLowerCase().includes(searchLower)
      );
    });
  }, [
    listeGraphes,
    interfaceAdmin,
    filtre_projects_list_id,
    project_id_url,
    searchValue,
  ]);

  return (
    <div className={S.container}>
      {/* Toolbar */}
      <Toolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchPlaceholder="recherche"
        onCreate={creer}
        createTooltip={checkMot('creer')}
        onRefresh={handleRefresh}
        refreshRequestType={requestType.recuperation_de_la_liste_des_graphes}
        refreshMessage={checkMot('maj_graphes_en_cour')}
      />

      {/* DataGrid */}
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.graph_id}
        onRowSelectionModelChange={handleSelectionModelChange}
        rowSelectionModel={selectionModel}
        pageSizeOptions={[25, 50, 100]}
        initialPageSize={25}
        autoRowHeight
      />
    </div>
  );
};

export default TableauGraphe;
